import { useState, useRef, useEffect, useCallback } from 'react'
import Draggable from 'react-draggable'
import cls from 'classnames'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { LiveKitRoom } from '@livekit/components-react'
import { DisconnectReason, MediaDeviceFailure } from 'livekit-client'

import {
  setFoldCall,
  TAccountData,
  useAppSelector,
  sendEndCallRequest,
  leaveVoiceRoomRequest,
  refreshCallTokenRequest,
  leaveExternalMeeting
} from 'store'
import config from 'config'
import { useAdaptiveLayout, usePrevious, useDidMountEffect } from 'App/hooks'
import { CallOutgoingFooterContainer } from 'App/containers'
import { CallStatus } from 'App/components'
import { toast } from 'App/components/ToastContainer'
import { toastDefaultOptions } from 'globalConstants'

import { ECallConnectionQuality, Room, asReact17 } from '../../LiveKitRoom'
import styles from '../Call.module.scss'

const LiveKit = asReact17(LiveKitRoom)

export type TUseCallCoreOptions = {
  avatarURL: string
  accountData: TAccountData | null

  callId?: string | null
  jwt?: string | null
  chatId?: string | null
  organizationId?: string | null
  startTime?: Date | null // call only
  fixed?: boolean
  isOwner?: boolean
  isVideoCall?: boolean
  isMicOn?: boolean
  isOneToOne?: boolean
  type?: 'call' | 'channel' | 'meeting'

  serverUrl?: string

  joinUrl?: string
}

const CANCEL_DRAGGABLE_SELECTOR = 'cancel-draggable-answered'

const DEVICE_FAILURES: MediaDeviceFailure[] = [
  MediaDeviceFailure.PermissionDenied,
  MediaDeviceFailure.NotFound,
  MediaDeviceFailure.DeviceInUse
]

const SERVER_INITIATED_DISCONNECTS: DisconnectReason[] = [
  DisconnectReason.DUPLICATE_IDENTITY,
  DisconnectReason.PARTICIPANT_REMOVED,
  DisconnectReason.ROOM_DELETED,
  DisconnectReason.SERVER_SHUTDOWN
]

export const useCallCore = ({
  startTime,
  jwt,
  callId,
  fixed = true,
  isOwner = false,
  isVideoCall = false,
  isMicOn = true,
  type = 'call',
  serverUrl,
  joinUrl
}: TUseCallCoreOptions) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isMobile } = useAdaptiveLayout()
  const rootRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false)

  const [isAnsweredCall, setAnsweredCall] = useState<boolean>(false)
  const isMobilePrev = usePrevious(isMobile)
  const [connectionQuality, setConnectionQuality] = useState<ECallConnectionQuality>(
    ECallConnectionQuality.GOOD
  )

  const hasLeftRef = useRef(false)

  const hasRefreshedRef = useRef(false)

  const isFolded = useAppSelector((state) => state.calls.isFolded)

  const hangupLabel =
    isOwner && type !== 'meeting'
      ? t('chat.call.toolbar.endCall')
      : t('chat.call.toolbar.leaveCall')

  const isMobileFullScreen = isMobile && isFullscreen && !isFolded

  const isPageFullScreen = isFullscreen && !isMobile
  const isDraggable = !isFullscreen && fixed

  const endCallOnServer = useCallback(() => {
    if (hasLeftRef.current) {
      return
    }

    if (type === 'meeting') {
      hasLeftRef.current = true
      dispatch(leaveExternalMeeting())

      return
    }

    if (!callId) {
      return
    }

    hasLeftRef.current = true

    type === 'call'
      ? dispatch(sendEndCallRequest({ callId }))
      : dispatch(leaveVoiceRoomRequest({ voiceRoomId: callId }))
  }, [callId, dispatch, type])

  const handleConnected = useCallback(() => {
    hasRefreshedRef.current = false
  }, [])

  const handleError = useCallback(
    (error: Error) => {
      const failure = MediaDeviceFailure.getFailure(error)

      if (failure && DEVICE_FAILURES.includes(failure)) {
        return
      }

      if (type === 'meeting') {
        if (!hasLeftRef.current) {
          hasLeftRef.current = true
          toast.error(t('chat.call.meetingConnectionFailed'), toastDefaultOptions)
          dispatch(leaveExternalMeeting())
        }

        return
      }

      if (!callId || hasRefreshedRef.current) {
        return
      }

      hasRefreshedRef.current = true
      dispatch(refreshCallTokenRequest({ callId, type }))
    },
    [callId, dispatch, type, t]
  )

  const handleDisconnected = useCallback(
    (reason?: DisconnectReason) => {
      if (reason === DisconnectReason.CLIENT_INITIATED) {
        return
      }

      if (reason !== undefined && SERVER_INITIATED_DISCONNECTS.includes(reason)) {
        hasLeftRef.current = true

        return
      }

      endCallOnServer()
    },
    [endCallOnServer]
  )

  const handleFullscreen = useCallback(() => setIsFullscreen((expanded) => !expanded), [])

  const handleHideFullscreenOnMobile = useCallback(() => {
    setIsFullscreen(false)
    dispatch(setFoldCall({ isFolded: true }))
  }, [dispatch])

  const handleShowFullscreenOnMobile = useCallback(() => {
    setIsFullscreen(true)
    dispatch(setFoldCall({ isFolded: false }))
  }, [dispatch])

  const handleFullscreenOnMobile = useCallback(
    () => (isFullscreen ? handleHideFullscreenOnMobile() : handleShowFullscreenOnMobile()),
    [handleHideFullscreenOnMobile, handleShowFullscreenOnMobile, isFullscreen]
  )

  useEffect(
    () => {
      if (isMobile) {
        // When on the phone we connect to the call, we go into full screen view
        if (!isMobileFullScreen && type !== 'channel' && !isFolded) {
          handleShowFullscreenOnMobile()
        }

        return
      }

      if (type === 'meeting') {
        setIsFullscreen(true)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] /* Only on the mount */
  )

  useDidMountEffect(() => {
    if (isMobile && !isMobileFullScreen && !isFolded) {
      handleShowFullscreenOnMobile()
    }
  }, [isFolded])

  useEffect(() => {
    // For the desktop in the mobile view.
    // When the screen size becomes larger than the mobile screen
    if (isMobilePrev && !isMobile && !isMobileFullScreen && isFullscreen) {
      handleHideFullscreenOnMobile()
    }
  }, [handleHideFullscreenOnMobile, isFullscreen, isMobileFullScreen, isMobilePrev, isMobile])

  useEffect(() => {
    // For the desktop in the mobile view.
    // When the screen becomes smaller than the screen of a tablet device
    if (!isMobilePrev && isMobile && !isMobileFullScreen && isFullscreen) {
      handleHideFullscreenOnMobile()
    }
  }, [handleHideFullscreenOnMobile, isFullscreen, isMobileFullScreen, isMobile, isMobilePrev])

  useEffect(() => {
    document.body.style.overflow = isMobileFullScreen ? 'hidden' : ''
  }, [isMobileFullScreen])

  useEffect(
    () => () => {
      document.body.style.overflow = ''
    },
    []
  )

  const render = () => (
    <Draggable
      bounds="body"
      disabled={isFullscreen || isMobile || !fixed}
      cancel={`.${CANCEL_DRAGGABLE_SELECTOR}`}
      position={isMobile || !fixed || isPageFullScreen ? { x: 0, y: 0 } : undefined}
    >
      <div
        className={cls(styles.container, {
          [styles.mobileFullScreen]: isMobileFullScreen || isPageFullScreen,
          [styles.static]: !fixed,
          [styles.fullscreen]: isFullscreen
        })}
      >
        <div className={styles.videoCallContainer} ref={rootRef}>
          {jwt && (
            <LiveKit
              key={callId ?? jwt}
              serverUrl={serverUrl ?? config.livekitUrl}
              token={jwt}
              connect={true}
              audio={isMicOn}
              video={isVideoCall}
              onDisconnected={handleDisconnected}
              onConnected={handleConnected}
              onError={handleError}
              className={styles.callRoom}
              data-lk-theme="default"
            >
              <Room
                onHangup={endCallOnServer}
                onConnectionQualityChange={setConnectionQuality}
                hangupLabel={hangupLabel}
                buttonClassName={CANCEL_DRAGGABLE_SELECTOR}
                joinUrl={joinUrl}
                onToggleFullscreen={isMobile ? undefined : handleFullscreen}
                isFullscreen={isFullscreen}
                isCompact={!isFullscreen && fixed}
              />
            </LiveKit>
          )}

          {/*
           * CallOutsideButtonContainer — the "go to chat / case / organization"
           * strip — is not rendered here: in the full-page room it sits under
           * the toolbar. It is kept in App/containers for when the room gets a
           * chat of its own; chatId and organizationId are still on
           * TUseCallCoreOptions for the same reason.
           */}

          {isDraggable && (
            <>
              <div className={styles.draggableBoxTop} />
              <div className={styles.draggableBoxLeft} />
              <div className={styles.draggableBoxRight} />
              <div className={styles.draggableBoxFooter} />
            </>
          )}

          {isMobileFullScreen && (
            <div className={styles.callStatusWrapper}>
              <CallStatus
                isAnsweredCall={isAnsweredCall}
                startTime={startTime}
                connectionQuality={connectionQuality}
              />
            </div>
          )}

          {callId && (
            <CallOutgoingFooterContainer
              callId={callId}
              isCallOwner={isOwner}
              isAnsweredCall={isAnsweredCall}
              connectionQuality={connectionQuality}
              isFullscreen={isFullscreen}
              cancelDraggableClassName={CANCEL_DRAGGABLE_SELECTOR}
              rootRef={rootRef}
              fixed={fixed}
              onFullscreen={isMobile ? handleFullscreenOnMobile : handleFullscreen}
              type={type}
            />
          )}
        </div>
      </div>
    </Draggable>
  )

  return {
    render,
    rootRef,
    isFullscreen,
    setIsFullscreen,
    setAnsweredCall
  }
}
